import { computed } from "vue";
import { useOnboardingStore } from "../services";
import { EFormType, EPolicyType, isClaimOnboarding } from "../types";

/**
 * Composable for determining the current onboarding flow type.
 * Used to conditionally render components based on flow type.
 */
export const useFlowType = () => {
  const store = useOnboardingStore();

  /**
   * Current form type from the onboarding model
   */
  const formType = computed<string>(() => {
    return store.model?.type ?? EFormType.FLEET_ONBOARDING;
  });

  /**
   * Whether the invite is FAST_TRACK (bypass first step).
   */
  const isFastTrack = computed<boolean>(() => {
    return store.model?.journeyType === "FAST_TRACK";
  });

  /**
   * Whether the policy invite type is QUOTING (quote).
   * When false or unknown, treat as SERVICE (service policy).
   */
  const isQuotePolicy = computed<boolean>(() => {
    const policyType =
      store.model?.sections?.insurer?.data?.current_policy?.policy_type;

    return policyType === EPolicyType.QUOTING;
  });

  /**
   * Whether to show the "Current insurance info" section.
   * - fastTrack + quote (QUOTING): skip section (data filling issues).
   * - fastTrack + service (SERVICE): show section.
   * - normal flow: show section.
   */
  const showCurrentInsuranceSection = computed<boolean>(() => {
    if (!isFastTrack.value) {
      return true;
    }

    return !isQuotePolicy.value;
  });

  /**
   * Whether the current flow is a claim onboarding flow
   * (for defendants/claimants providing accident details)
   */
  const isDefendantOrClaimantFlow = computed<boolean>(() => {
    return isClaimOnboarding(formType.value);
  });

  /**
   * Whether the current flow is the standard fleet onboarding flow
   */
  const isFleetFlow = computed<boolean>(() => {
    return !isDefendantOrClaimantFlow.value;
  });

  /**
   * Get the initiator role from the access array
   * Used to determine consent language variation (e.g., TPA vs INSURER)
   */
  const initiatorRole = computed<string | null>(() => {
    const access = store.model?.access;

    if (access && access.length > 0) {
      return access[0].role;
    }

    return null;
  });

  /**
   * Whether the initiator is a TPA (Third Party Administrator)
   */
  const isTpaInitiated = computed<boolean>(() => {
    // TPA role check - for now, check if role is TPA or if it's a specific pattern
    // Backend may use "INSURER" until TPA role is created
    return initiatorRole.value === "TPA";
  });

  const isAttorneyFlow = computed<boolean>(() => {
    return initiatorRole.value === "LAW_FIRM";
  });

  /**
   * Get the initiator company name for display in consent/summary
   */
  const initiatorName = computed<string>(() => {
    const access = store.model?.access;

    if (access && access.length > 0) {
      return access[0].name;
    }

    return "";
  });

  /**
   * Get the initiator email for display in consent/summary
   */
  const initiatorEmail = computed<string>(() => {
    const access = store.model?.access;

    if (access && access.length > 0) {
      return access[0].email;
    }

    return "";
  });

  return {
    formType,
    isClaimFlow: isDefendantOrClaimantFlow,
    isFleetFlow,
    isFastTrack,
    isQuotePolicy,
    showCurrentInsuranceSection,
    initiatorRole,
    isTpaInitiated,
    isAttorneyFlow,
    initiatorName,
    initiatorEmail,
  };
};
